import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import Support from "@/pages/Support";
import ComingSoon from "@/pages/ComingSoon";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import ForgotPassword from "@/pages/ForgotPassword";
import EmailVerification from "@/pages/EmailVerification";
import TwoFactor from "@/pages/TwoFactor";
import Dashboard from "@/pages/Dashboard";
import WalletPage from "@/pages/Wallet";
import SendMoney from "@/pages/SendMoney";
import Receive from "@/pages/Receive";
import Transactions from "@/pages/Transactions";
import TransactionDetail from "@/pages/TransactionDetail";
import Beneficiaries from "@/pages/Beneficiaries";
import Kyc from "@/pages/KYC";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import LegalDocPage from "@/pages/legal/LegalDocPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminFinanceDashboard from "@/pages/AdminFinanceDashboard";

// Launch audit Task 5 — the entire Business* page suite (registration +
// 17 in-app pages) was never audited this session. Rather than expose an
// unverified area on a real financial site, every business path is routed
// to ComingSoon below instead of importing/rendering the real pages —
// the page files themselves are untouched on disk for future activation.

function FullPageLoader() {
  return (
    <div className="min-h-screen grid place-items-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-navy/15 border-t-emerald animate-spin" />
        <span className="text-sm text-muted-foreground">Loading MoneyMeet…</span>
      </div>
    </div>
  );
}

function Protected() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function AdminOnly() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app/dashboard" replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/app/dashboard"} replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: "'Inter', system-ui, sans-serif",
              borderRadius: "14px",
              border: "1px solid hsl(var(--border))",
            },
          }}
        />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/support" element={<Support />} />
          <Route path="/about"   element={<ComingSoon />} />
          <Route path="/blog"    element={<ComingSoon />} />
          <Route path="/careers" element={<ComingSoon />} />
          <Route path="/press"   element={<ComingSoon />} />
          <Route path="/docs"    element={<ComingSoon />} />
          <Route path="/legal/:docKey" element={<LegalDocPage />} />

          {/* Public auth routes */}
          <Route path="/login"        element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register"          element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/register/business" element={<ComingSoon />} />
          <Route path="/forgot"       element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/2fa"          element={<TwoFactor />} />
          <Route path="/onboarding"   element={<Onboarding />} />
          <Route path="/verify-email" element={<EmailVerification />} />

          {/* Protected app routes */}
          <Route element={<Protected />}>
            <Route path="/app"                    element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/app/dashboard"          element={<Dashboard />} />
            <Route path="/app/wallet"             element={<WalletPage />} />
            <Route path="/app/send"               element={<SendMoney />} />
            <Route path="/app/receive"            element={<Receive />} />
            <Route path="/app/transactions"       element={<Transactions />} />
            <Route path="/app/transactions/:id"   element={<TransactionDetail />} />
            <Route path="/app/beneficiaries"      element={<Beneficiaries />} />
            <Route path="/app/kyc"                element={<Kyc />} />
            <Route path="/app/profile"            element={<Profile />} />
            <Route path="/app/settings"           element={<Settings />} />
            <Route path="/app/notifications"      element={<Notifications />} />

            {/* Business routes — see the launch-audit note above the imports:
                every business path shows ComingSoon, unaudited this session. */}
            <Route path="/app/business/*"                element={<ComingSoon />} />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminOnly />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/finance" element={<AdminFinanceDashboard />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
